import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile, 
  User, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  Auth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult
} from 'firebase/auth';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, filter, timeout } from 'rxjs';
import { Router } from '@angular/router';
import { UserService, UserData } from './user.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private userService = inject(UserService);
  private http = inject(HttpClient);
  
  private app = initializeApp(environment.firebaseConfig);
  private auth = getAuth(this.app);
  private googleProvider = new GoogleAuthProvider();

  private currentUserSubject = new BehaviorSubject<User | null | undefined>(undefined);
  public currentUser$: Observable<User | null | undefined> = this.currentUserSubject.asObservable();

  private userProfileSubject = new BehaviorSubject<UserData | null | undefined>(undefined);
  public userProfile$: Observable<UserData | null | undefined> = this.userProfileSubject.asObservable();

  private confirmationResult?: ConfirmationResult;
  private recaptchaVerifier?: RecaptchaVerifier;
  private recaptchaContainerEl?: HTMLDivElement;
  private recaptchaInitPromise?: Promise<void>;
  private isHandledExternally = false;

  constructor() {
    // Enable this for local testing to bypass INVALID_APP_CREDENTIAL error
    const host = window.location.hostname;
    if (!environment.production || host === 'localhost' || host === '127.0.0.1') {
      this.auth.settings.appVerificationDisabledForTesting = true;
      console.log('Firebase app verification disabled for testing');
    }

    onAuthStateChanged(this.auth, (user) => {
      this.currentUserSubject.next(user);
      if (user) {
        if (!this.isHandledExternally) {
          console.log('onAuthStateChanged: User logged in, refreshing profile automatically');
          this.refreshUserProfile();
        } else {
          console.log('onAuthStateChanged: isHandledExternally is true, skipping automatic profile refresh');
        }
      } else {
        this.userProfileSubject.next(null);
      }
    });

    // Start initializing Recaptcha in the background so it's ready when the user clicks "Send OTP"
    this.initRecaptcha();
  }

  async refreshUserProfile(): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      try {
        const profile = await firstValueFrom(this.userService.getUser(user.uid));
        this.userProfileSubject.next(profile);
      } catch (error) {
        console.error('Error refreshing user profile:', error);
        this.userProfileSubject.next(null);
      }
    } else {
      this.userProfileSubject.next(null);
    }
  }

  async signup(email: string, password: string, name: string): Promise<void> {
    this.isHandledExternally = true;
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      await updateProfile(userCredential.user, { displayName: name });
      
      // Save user to backend and update local profile state
      const profile = await firstValueFrom(this.userService.createUser({
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: name,
        createdAt: new Date().toISOString(),
        referralSource: typeof window !== 'undefined' ? sessionStorage.getItem('referral_source') : null
      }));
      this.userProfileSubject.next(profile);

      // Navigate based on role
      await this.handleRoleBasedRedirect(userCredential.user.uid);
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    } finally {
      this.isHandledExternally = false;
    }
  }

  async signin(email: string, password: string): Promise<void> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      await this.handleRoleBasedRedirect(userCredential.user.uid);
    } catch (error) {
      console.error('Signin error:', error);
      throw error;
    }
  }

  async googleSignin(): Promise<void> {
    this.isHandledExternally = true;
    try {
      const userCredential = await signInWithPopup(this.auth, this.googleProvider);
      const uid = userCredential.user.uid;

      let profile: UserData;
      try {
        console.log('Attempting to fetch existing user profile for:', uid);
        profile = await firstValueFrom(this.userService.getUser(uid));
        console.log('Existing user profile found:', profile.role);
      } catch (error: any) {
        // Only create if user definitely doesn't exist (404)
        if (error.status === 404) {
          console.log('User profile not found (404), creating new user for:', uid);
          profile = await firstValueFrom(this.userService.createUser({
            uid: uid,
            email: userCredential.user.email,
            displayName: userCredential.user.displayName,
            createdAt: new Date().toISOString(),
            referralSource: typeof window !== 'undefined' ? sessionStorage.getItem('referral_source') : null
          }));
        } else {
          console.error('Error fetching user profile (non-404), skipping creation:', error);
          // Wait for the constructor's refreshUserProfile or re-throw
          throw error;
        }
      }

      this.userProfileSubject.next(profile);
      await this.handleRoleBasedRedirect(uid);
    } catch (error) {
      console.error('Google Signin error:', error);
      throw error;
    } finally {
      this.isHandledExternally = false;
    }
  }

  private initRecaptcha(): Promise<void> {
    if (this.recaptchaInitPromise) {
      return this.recaptchaInitPromise;
    }

    this.recaptchaInitPromise = new Promise(async (resolve, reject) => {
      try {
        // Create an isolated DOM element for the invisible recaptcha
        this.recaptchaContainerEl = document.createElement('div');
        this.recaptchaContainerEl.id = `recaptcha-cache-${Date.now()}`;
        this.recaptchaContainerEl.style.display = 'none';
        document.body.appendChild(this.recaptchaContainerEl);

        this.recaptchaVerifier = new RecaptchaVerifier(this.auth, this.recaptchaContainerEl, {
          size: 'invisible',
          callback: () => {
             // reCAPTCHA solved
          },
          'expired-callback': () => {
             // Reset the cache when expired
             this.resetRecaptcha();
          }
        });

        // Pre-render logic
        await this.recaptchaVerifier.render();
        resolve();
      } catch (error) {
        console.error('Failed to pre-initialize Recaptcha:', error);
        this.resetRecaptcha();
        reject(error);
      }
    });

    return this.recaptchaInitPromise;
  }

  private resetRecaptcha(): void {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (e) {
        /* Ignore */
      }
      this.recaptchaVerifier = undefined;
    }
    if (this.recaptchaContainerEl) {
      try {
        this.recaptchaContainerEl.remove();
      } catch (e) {
        /* Ignore */
      }
      this.recaptchaContainerEl = undefined;
    }
    this.recaptchaInitPromise = undefined;
    // Immediately start re-initializing
    this.initRecaptcha();
  }

  async signInWithPhone(phoneNumber: string, _ignoredContainerIdStyle: string): Promise<void> {
    try {
      // Normalize phone number
      let normalizedPhone = phoneNumber.trim().replace(/\s+/g, '');
      if (/^\d{10}$/.test(normalizedPhone)) {
        normalizedPhone = '+91' + normalizedPhone;
      }
      if (!normalizedPhone.startsWith('+')) {
        normalizedPhone = '+' + normalizedPhone;
      }

      // Ensure recaptcha is fully ready
      await this.initRecaptcha();

      if (!this.recaptchaVerifier) {
         throw new Error("Recaptcha is not initialized");
      }

      // Execute network verify explicitly before requesting OTP
      await this.recaptchaVerifier.verify();

      this.confirmationResult = await signInWithPhoneNumber(this.auth, normalizedPhone, this.recaptchaVerifier);
      
    } catch (error) {
      console.error('Phone sign-in error:', error);
      // If we hit an error (like a timeout or invalid token), toss the verifier and build a new one
      this.resetRecaptcha();
      throw error;
    }
  }

  async verifyOtp(otp: string, displayName?: string | null): Promise<void> {
    if (!this.confirmationResult) {
      throw new Error('No phone verification in progress');
    }

    this.isHandledExternally = true;
    try {
      const userCredential = await this.confirmationResult.confirm(otp);
      const uid = userCredential.user.uid;

      let profile: UserData;
      try {
        console.log('Fetching user profile after OTP verification for:', uid);
        profile = await firstValueFrom(this.userService.getUser(uid));
        console.log('User profile already exists:', profile.role);
      } catch (error: any) {
        if (error.status === 404) {
          console.log('Profile not found (404), creating new profile for phone user:', uid);
          // Create new user profile for phone auth
          profile = await firstValueFrom(this.userService.createUser({
            uid: uid,
            email: null,
            phoneNumber: userCredential.user.phoneNumber || undefined,
            displayName: displayName || null,
            createdAt: new Date().toISOString(),
            referralSource: typeof window !== 'undefined' ? sessionStorage.getItem('referral_source') : null
          }));
          console.log('Profile created successfully:', profile);
        } else {
          console.error('Error fetching/creating user profile during OTP verification:', error);
          throw error;
        }
      }

      this.userProfileSubject.next(profile);
      await this.handleRoleBasedRedirect(uid);
    } catch (error) {
      console.error('OTP verification error:', error);
      throw error;
    } finally {
      this.isHandledExternally = false;
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut(this.auth);
      this.router.navigate(['/signin']);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${environment.backendUrl}/auth/forgot-password`, { email }));
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  async verifyResetCode(code: string): Promise<string> {
    try {
      return await verifyPasswordResetCode(this.auth, code);
    } catch (error) {
      console.error('Verify reset code error:', error);
      throw error;
    }
  }

  async confirmReset(code: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(this.auth, code, newPassword);
    } catch (error) {
      console.error('Confirm password reset error:', error);
      throw error;
    }
  }

  get currentUserValue(): User | null | undefined {
    return this.currentUserSubject.value;
  }

  private async handleRoleBasedRedirect(uid: string): Promise<void> {
    try {
      // Wait for the profile to be loaded by the constructor's onAuthStateChanged
      const userData = await firstValueFrom(
        this.userProfile$.pipe(
          filter(profile => !!profile),
          timeout(5000)
        )
      );

      if (userData?.role === 'aarvya_admin') {
        this.router.navigate(['/admin/dashboard']);
      }
      // If not admin, we let the AuthModalComponent handle any specific redirection 
      // (like /checkout) or simply stay on the current page.
    } catch (error) {
      console.error('Error waiting for user role:', error);
    }
  }
}
